import { createFileRoute } from "@tanstack/react-router";
import { physicianPageSchema, safeJsonLd } from "@/lib/structured-data-core";
import { lazy, Suspense, useState } from "react";
import { User, GraduationCap } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { SocialPosts } from "@/components/site/SocialPosts";
import hero from "@/assets/restore/saleh-hero.webp.asset.json";
import bioImg from "@/assets/restore/saleh-bio.webp.asset.json";
import { absoluteUrl } from "@/lib/absolute-url";
import { QuoteBand } from "@/components/site/QuoteBand";

// Same photo with stray edge pixels (top left) removed.
const HERO_SRC = "/img/r/saleh-hero-clean.webp";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
const Reviews = lazy(() => import("@/components/site/Reviews").then(m => ({ default: m.Reviews })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

const DESCRIPTION =
  "Dr. Shadia Saleh is a hair restoration physician trained by Dr. James Harris, with a background in hair loss research. She plans natural-looking results.";

export const Route = createFileRoute("/doctors/dr-shadia-saleh")({
  head: () => ({
    meta: [
      { title: "Dr. Shadia Saleh — Hair Restoration Physician | RESTORE® Hair" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Dr. Shadia Saleh — Hair Restoration Physician" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "profile" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: absoluteUrl(hero.url) },
      { name: "twitter:image", content: absoluteUrl(hero.url) },
    ],
    links: [{ rel: "canonical", href: "https://restorehair.com/doctors/dr-shadia-saleh/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd(
          physicianPageSchema({
            name: "Dr. Shadia Saleh",
            jobTitle: "Hair Restoration Physician",
            description: DESCRIPTION,
            image: hero.url,
            pageUrl: "https://restorehair.com/doctors/dr-shadia-saleh/",
          }),
        ),
      },
    ],
  }),
  component: DrSalehPage,
});

function DrSalehPage() {
  const [tab, setTab] = useState<"bio" | "education">("bio");

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main data-cta-location="doctor">
        <section className="overflow-hidden bg-background">
          {/* Her photo is a head-and-shoulders crop, not full length like the other doctors,
              so it is shown smaller and the text is centred beside it. */}
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 pt-10 lg:grid-cols-2 lg:items-center lg:gap-4">
            <div className="pb-10 lg:pb-16">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
                Dr. Shadia Saleh
              </h1>
              <p className="mt-3 text-lg md:text-xl font-semibold text-primary">
                Hair Restoration Physician
              </p>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-muted-foreground">
                {DESCRIPTION}
              </p>
            </div>
            <div className="flex justify-center lg:justify-end lg:self-end">
              <img
                src={HERO_SRC}
                alt="Dr. Shadia Saleh — Hair Restoration Physician"
                width={440}
                height={568}
                className="h-auto w-full max-w-[300px] object-contain sm:max-w-sm"
                loading="eager"
              />
            </div>
          </div>
        </section>
        <QuoteBand />

        <section className="bg-muted/40 py-12 md:py-16">
          <div className="mx-auto w-full max-w-6xl px-4">
            <div className="grid max-w-3xl grid-cols-2 gap-1">
              <button
                type="button"
                onClick={() => setTab("bio")}
                className={`inline-flex items-center justify-center gap-2 px-6 py-5 text-lg font-semibold transition ${
                  tab === "bio"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-foreground shadow-sm hover:bg-card/80"
                }`}
              >
                <User size={20} />
                Full Bio
              </button>
              <button
                type="button"
                onClick={() => setTab("education")}
                className={`inline-flex items-center justify-center gap-2 px-6 py-5 text-lg font-semibold transition ${
                  tab === "education"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-foreground shadow-sm hover:bg-card/80"
                }`}
              >
                <GraduationCap size={20} />
                Education
              </button>
            </div>

            <div className="bg-card px-4 py-10 md:px-10 md:py-14 shadow-sm">
              {tab === "bio" ? (
                <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 md:grid-cols-[2fr_1fr] md:items-start">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                      Dr. Shadia Saleh's Bio
                    </h2>
                    <div className="mt-4 space-y-4 text-base leading-relaxed text-muted-foreground">
                      <p>
                        Dr. Shadia Saleh was handpicked and trained by Dr. James Harris, medical
                        director of RESTORE®. Her surgical philosophy centers on blending artistry
                        with science while treating patients with compassion. She strives to help
                        patients achieve natural, individualized results that support a renewed
                        sense of self.
                      </p>
                      <p>
                        Prior to joining RESTORE®, Dr. Saleh conducted clinical research in hair
                        loss focused on identifying potential risk factors associated with
                        scarring alopecia. She also contributed to the development of a novel
                        laboratory technique for isolating Extracellular Vesicles (EVs) from
                        biological matrices — work relevant to advancing academic research in
                        human disease and to the development of therapeutic drug-delivery
                        mechanisms.
                      </p>
                    </div>
                  </div>
                  <img
                    src={bioImg.url}
                    alt="Dr. Shadia Saleh"
                    loading="lazy"
                    className="w-full max-w-sm justify-self-center object-contain"
                  />
                </div>
              ) : (
                <div className="mx-auto max-w-6xl">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                    Dr. Shadia Saleh, Education, Residency &amp; Experience
                  </h2>
                  <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-md border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">Undergraduate</p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        New Jersey Institute of Technology
                      </p>
                    </div>
                    <div className="rounded-md border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">Training</p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        University of Kansas School of Medicine
                      </p>
                    </div>
                    <div className="rounded-md border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">Medical School</p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        University of Kansas School of Medicine
                      </p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        With Global Scholar Distinction
                      </p>
                    </div>
                    <div className="rounded-md border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">
                        Medical Licensure
                      </p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        New York, Florida, Texas
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <Suspense fallback={null}>
          <Results resultsHref="https://restorehair.com/results/" resultsButtonLabel="See More Results" />
          <Reviews />
          <SocialPosts />
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
